import * as React from "react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

/**
 * BrandCard Component - Clean White Card Design
 *
 * A clean, light-mode Card component for the platform with:
 * - White card surfaces
 * - Subtle border and shadow accents
 * - Brand-color left/top border variants
 * - Accessible hover states
 * - Accessibility compliant (WCAG AA)
 *
 * Built on top of shadcn/ui Card for consistency
 */

export type BrandCardVariant =
  | "primary" // Primary card - main content
  | "glass" // Glass card - stats/metrics
  | "data" // Data card - financial metrics
  | "premium" // Premium card - featured assets, high-value
  | "outlined" // Outlined - secondary content
  | "sapphire"; // Sapphire variant - information/trust

export type BrandCardGlow = "none" | "subtle" | "strong";

export interface BrandCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant of the card
   */
  variant?: BrandCardVariant;

  /**
   * Whether to show gold glow effect on hover
   */
  glow?: BrandCardGlow;

  /**
   * Whether to add professional hover effects
   */
  hoverable?: boolean;

  /**
   * Market type for color coding (optional override)
   */
  marketType?: "auction" | "uniswap" | "pendle" | "governance";
}

/**
 * BrandCard Component - Clean White Card
 */
export const BrandCard = React.forwardRef<HTMLDivElement, BrandCardProps>(
  (
    {
      className,
      variant = "primary",
      glow = "none",
      hoverable = false,
      marketType,
      children,
      ...props
    },
    ref,
  ) => {
    // Clean White Card Variants
    const variantStyles: Record<BrandCardVariant, string> = {
      // Primary Card - for main content
      primary: "bg-white border border-border-card shadow-card",

      // Glass Card - for stats, metrics
      glass: "bg-white border border-border-card",

      // Data Card - for financial metrics
      data: "bg-white border border-border-card border-l-4 border-l-brand-primary",

      // Premium Card - for featured assets, high-value
      premium:
        "bg-white border border-border-card border-t-4 border-t-brand-accent",

      // Outlined - for secondary content
      outlined: "bg-white border border-border-card",

      // Sapphire - for information, trust indicators
      sapphire:
        "bg-white border border-border-card border-l-4 border-l-brand-primary-light",
    };

    // Market type overrides (subtle left-border accents)
    const marketStyles: Record<string, string> = {
      auction: "border-l-4 border-l-warning",
      uniswap: "border-l-4 border-l-info",
      pendle: "border-l-4 border-l-purple-500",
      governance: "border-l-4 border-l-brand-primary",
    };

    // Hover effects
    const glowStyles: Record<BrandCardGlow, string> = {
      none: "",
      subtle: "hover:shadow-card-hover transition-shadow duration-300",
      strong:
        "hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300",
    };

    return (
      <Card
        ref={ref}
        className={cn(
          // Base styles - professional
          "relative overflow-hidden transition-all duration-300 ease-in-out",

          // Apply variant styles (always), plus market type border accent if provided
          variantStyles[variant],
          marketType && marketStyles[marketType],

          // Glow effects
          glowStyles[glow],

          // Hover affordance — sharp border-color + tiny lift instead of
          // scale. Scale ("breathing") reads as mobile-app jiggle on a
          // grid of financial cards; border + 2px lift reads as "designed".
          hoverable &&
            "cursor-pointer hover:border-slate-300 hover:-translate-y-0.5 transition-[border-color,transform] duration-200 ease-out",

          // Custom classes
          className,
        )}
        {...props}
      >
        {children}
      </Card>
    );
  },
);

BrandCard.displayName = "BrandCard";

/**
 * Specialized Card Variants
 */

/**
 * GlassCard - for overlay elements, quick stats
 */
export const GlassCard = React.forwardRef<
  HTMLDivElement,
  Omit<BrandCardProps, "variant">
>((props, ref) => <BrandCard ref={ref} variant="glass" {...props} />);

GlassCard.displayName = "GlassCard";

/**
 * PremiumCard - for featured assets, high-value content
 */
export const PremiumCard = React.forwardRef<
  HTMLDivElement,
  Omit<BrandCardProps, "variant">
>((props, ref) => (
  <BrandCard ref={ref} variant="premium" glow="subtle" {...props} />
));

PremiumCard.displayName = "PremiumCard";

/**
 * StatCard - Professional Financial Metrics Display
 */
export interface StatCardProps extends BrandCardProps {
  /**
   * Stat label
   */
  label: string;

  /**
   * Stat value
   */
  value: string | number;

  /**
   * Optional icon
   */
  icon?: React.ReactNode;

  /**
   * Optional change indicator
   */
  change?: {
    value: number;
    period?: string;
  };

  /**
   * Trend direction (overrides change calculation)
   */
  trend?: "up" | "down" | "neutral";

  /**
   * Visual selected state - shows ring + glow + scale effect
   */
  selected?: boolean;
}

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      label,
      value,
      icon,
      change,
      trend,
      selected,
      className,
      children,
      variant = "glass",
      ...props
    },
    ref,
  ) => {
    // Determine trend
    const trendDirection =
      trend ||
      (change && change.value >= 0 ? "up" : change ? "down" : "neutral");
    const isPositive = trendDirection === "up";
    const isNegative = trendDirection === "down";

    return (
      <BrandCard
        ref={ref}
        variant={variant}
        className={cn(
          "p-6 group",
          // Selected state styles
          selected && [
            "ring-2 ring-brand-primary/60",
            "scale-[1.02]",
            "shadow-card-hover",
          ],
          className,
        )}
        glow={selected ? "strong" : "subtle"}
        hoverable
        {...props}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-text-light font-medium uppercase tracking-wide">
            {label}
          </span>
          {icon && (
            <div className="text-brand-accent group-hover:scale-110 transition-transform duration-300">
              {icon}
            </div>
          )}
        </div>

        <div className="text-3xl font-bold text-text-dark font-mono tracking-tight tabular-nums">
          {value}
        </div>

        {change && (
          <div
            className={cn(
              "text-xs mt-2 font-semibold",
              isPositive && "text-success",
              isNegative && "text-danger",
              !isPositive && !isNegative && "text-text-muted",
            )}
          >
            {isPositive ? "+" : ""}
            {change.value.toFixed(2)}%
            {change.period && (
              <span className="ml-1 text-text-secondary font-normal">
                {change.period}
              </span>
            )}
          </div>
        )}

        {children}
      </BrandCard>
    );
  },
);

StatCard.displayName = "StatCard";

/**
 * MarketCard - for different market types
 */
export interface MarketCardProps extends Omit<BrandCardProps, "marketType"> {
  marketType: NonNullable<BrandCardProps["marketType"]>;
}

export const MarketCard = React.forwardRef<HTMLDivElement, MarketCardProps>(
  ({ marketType, className, children, ...props }, ref) => {
    return (
      <BrandCard
        ref={ref}
        marketType={marketType}
        className={cn("p-6 rounded-xl", className)}
        glow="subtle"
        hoverable
        {...props}
      >
        {children}
      </BrandCard>
    );
  },
);

MarketCard.displayName = "MarketCard";

/**
 * FeatureCard - for highlighting features, trust indicators
 */
export interface FeatureCardProps extends BrandCardProps {
  /**
   * Feature title
   */
  title: string;

  /**
   * Feature description
   */
  description: string;

  /**
   * Optional icon
   */
  icon?: React.ReactNode;
}

export const FeatureCard = React.forwardRef<HTMLDivElement, FeatureCardProps>(
  ({ title, description, icon, className, children, ...props }, ref) => {
    return (
      <BrandCard
        ref={ref}
        variant="outlined"
        className={cn("p-6 rounded-xl", className)}
        hoverable
        glow="subtle"
        {...props}
      >
        {icon && (
          <div className="mb-4 text-brand-accent w-12 h-12 flex items-center justify-center rounded-lg bg-brand-accent/10 border border-brand-accent/30">
            {icon}
          </div>
        )}

        <h3 className="text-xl font-semibold text-text-dark mb-2 tracking-wide">
          {title}
        </h3>

        <p className="text-text-light text-sm leading-relaxed">{description}</p>

        {children}
      </BrandCard>
    );
  },
);

FeatureCard.displayName = "FeatureCard";

/**
 * DataCard - for financial data display with professional styling
 */
export interface DataCardProps extends BrandCardProps {
  /**
   * Card title
   */
  title?: string;

  /**
   * Optional header action (e.g., menu, link)
   */
  headerAction?: React.ReactNode;
}

export const DataCard = React.forwardRef<HTMLDivElement, DataCardProps>(
  ({ title, headerAction, className, children, ...props }, ref) => {
    return (
      <BrandCard
        ref={ref}
        variant="data"
        className={cn("p-6 rounded-lg", className)}
        {...props}
      >
        {(title || headerAction) && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border-card">
            {title && (
              <h3 className="text-lg font-semibold text-text-dark">{title}</h3>
            )}
            {headerAction && (
              <div className="text-text-light">{headerAction}</div>
            )}
          </div>
        )}
        {children}
      </BrandCard>
    );
  },
);

DataCard.displayName = "DataCard";
